/* eslint-disable no-param-reassign */

/**
 * @file Surface state
 */
import { deleteWall, JsonAPIResource, saveWall } from '@padlet/arvo'
import { apolloClient } from 'bits/apollo'
import { deleteWallConfirmationParams } from 'bits/confirmation_dialog'
import { isAppUsing } from 'bits/flip'
import { isValidFolderName, MAX_FOLDER_NAME_LENGTH } from 'bits/folder'
import { __ } from 'bits/intl'
import { loginWithReferrerUrl, navigateTo, reload } from 'bits/location'
import { getNotificationPermission, registerForPushNotifications } from 'bits/notifications'
import { numberToHumanSize } from 'bits/numbers_helper'
import safeStorage from 'bits/safe_storage'
import { ARCHIVE_PADLET } from 'graphql/padlets'
import anxiousFace from 'images/anxious_face.svg'
import sortBy from 'lodash.sortby'
import { watchHash } from 'surface/location'
import PadletApi from 'surface/padlet_api'
import { Library } from 'types'
import Vue from 'vue'
import Vuex, { Plugin, StoreOptions } from 'vuex'
import appBarAccount from 'vuexstore/modules/app_bar_account'
import attachment from 'vuexstore/modules/attachment'
import browser from 'vuexstore/modules/browser'
import wallCollections from 'vuexstore/modules/collections'
import comment from 'vuexstore/modules/comment'
import composerModal from 'vuexstore/modules/composer_modal'
import composerModalAlert from 'vuexstore/modules/composer_modal_alert'
import composerModalTagInputs from 'vuexstore/modules/composer_modal_tag_inputs'
import contentPicker from 'vuexstore/modules/content_picker'
import contributingStatus from 'vuexstore/modules/contributing_status'
import draft from 'vuexstore/modules/draft'
import dragAndDropEvents from 'vuexstore/modules/drag_and_drop_events'
import electronApp from 'vuexstore/modules/electron_app'
import alertDialog from 'vuexstore/modules/global_alert_dialog'
import confirmationDialog from 'vuexstore/modules/global_confirmation_dialog'
import inputDialog from 'vuexstore/modules/global_input_dialog'
import globalSnackbar, { SnackbarNotificationType } from 'vuexstore/modules/global_snackbar'
import imagePicker from 'vuexstore/modules/image_picker'
import libraryPlans, { UpgradeStep as LibraryUpgradeStep } from 'vuexstore/modules/library_plans'
import map from 'vuexstore/modules/map'
import nativeApp from 'vuexstore/modules/native_app'
import onboarding from 'vuexstore/modules/onboarding'
import plans, { UpgradeSource, UpgradeStep } from 'vuexstore/modules/plans'
import post from 'vuexstore/modules/post'
import postAction from 'vuexstore/modules/post_action'
import postConnection from 'vuexstore/modules/post_connection'
import postFilter from 'vuexstore/modules/post_filter'
import reaction from 'vuexstore/modules/reaction'
import realtime from 'vuexstore/modules/realtime'
import section from 'vuexstore/modules/section'
import settings from 'vuexstore/modules/settings'
import share from 'vuexstore/modules/share'
import snackbars from 'vuexstore/modules/snackbars'
import table from 'vuexstore/modules/table'
import userContributor from 'vuexstore/modules/user_contributor'
import wallAccessSettings from 'vuexstore/modules/wall_access_settings'
import addressListener from 'vuexstore/plugins/address_listener'
import electronBridgeListener from 'vuexstore/plugins/electron_bridge_listener'
import logger, { shouldLog } from 'vuexstore/plugins/logger'
import { RootState, SurfaceState } from 'vuexstore/surface/types'

Vue.use(Vuex)
// How to decide what to put in state vs getters
// State should have either:
// 1. things we sync with the server
// 2. things that change
// 3. things that are a function of the device
// Everything else is in getters.

// Keep all lists alphabetical
const plugins: Array<Plugin<RootState>> = [
  addressListener,
  electronBridgeListener<RootState>('dashboard'),
  // pepinTracker    // Uncomment to track all state changes
]
if (shouldLog) plugins.push(logger)

export const blankState = (): SurfaceState => ({
  /* CONSTANTS */
  arvoConfig: undefined,
  canPostMessage: false,
  constants: {},
  cookiesEnabled: false,
  csrfToken: '',
  deviceId: '',
  dir: '',
  dpr: 1,
  environment: 'production',
  inlinePostEdit: false,
  isAndroid: false,
  isApp: false,
  isChrome: false,
  isDesktop: false,
  isElectronApp: false,
  isEdge: false,
  isEmbedded: false,
  isFirefox: false,
  isIE: false,
  isIOS: false,
  isIPhone: false,
  isIPhoneX: false,
  isMac: false,
  isMobile: false,
  isMousable: false,
  isNativeMenu: false,
  isOldApp: false,
  isPhone: false,
  isSafari: false,
  isTablet: false,
  isTouchable: false,
  isWindows: false,
  locale: 'en',
  modalPostEdit: false,
  nativePostEdit: false,
  release: '',
  uid: '',
  xHeader: false,
  homeUrl: '',

  /* OBJECTS */
  tenant: {},
  user: {},
  wall: {},

  /* OTHERS */
  commentsPanelPostId: null,
  convertLayoutErrorMessage: '',
  editorHeight: 0,
  fileThatExceededSize: null,
  headerHeight: 136,
  isOnline: true,
  isRefreshPending: false,
  wallLastSyncTime: null,
  wishListMounted: false,
  xContributorsList: false,
  xExtensionRequired: false,
  xWallQuotaExceeded: false,
  xEditBookmarksDialog: false,
  editBookmarksDialogPopoverAnchor: null,
  isAPostDragging: false,

  /* PANELS */
  xCommentsPanel: false,
  xConvertLayoutPanel: false,
  xInfo: false,
  xPermitNotification: false,
  xReloadNotification: false,
  xRemakePanel: false,
  xToolMenu: false,
  isSkoletubeUser: false,

  /* PERMISSIONS */
  amIOwner: false,
  amILibraryOwner: false,
  canIAdminister: false,
  canIEdit: false,
  canIMakeWalls: false,
  canIModerate: false,
  canIRemake: false,
  canIWrite: false,
  isSubscriptionPaused: false,
  isReadOnly: true,
  isScreenshotMode: false,
  amICollaborator: false,

  /* LIBRARIES */
  library: {},
  creatableLibraries: [], // Libraries where user can create
  viewableLibraries: [], // Libraries where user can view

  /* EMBED */
  loginAndRedirectToWallUrl: '',
})

export const storeStructure: StoreOptions<RootState> = {
  plugins,
  modules: {
    attachment,
    appBarAccount,
    nativeApp,
    imagePicker,
    reaction,
    postConnection,
    comment,
    section,
    post,
    postAction,
    postFilter,
    table,
    share,
    settings,
    wallAccessSettings,
    onboarding,
    contentPicker,
    confirmationDialog,
    alertDialog,
    inputDialog,
    map,
    snackbars,
    globalSnackbar,
    realtime,
    browser,
    userContributor,
    composerModal,
    draft,
    composerModalAlert,
    composerModalTagInputs,
    electronApp,
    dragAndDropEvents,
    contributingStatus,
    plans,
    libraryPlans,
    wallCollections,
  },
  state: blankState() as RootState,
  getters: {
    // constants
    useArvo: (state) => !!state.constants.useArvo,
    // wall
    displayAttrs: (state, getters) =>
      getters['settings/isPreviewing'] ? getters['settings/previewAttributes'] : state.wall,
    wallId: (state) => state.wall.id,
    wallUpdatedAt: (state) => state.wall.updated_at,
    wallCreatedAt: (state) => state.wall.created_at,
    originalBackground: (state) => state.wall.background,
    title: (state, getters) => getters.displayAttrs.title,
    description: (state, getters) => getters.displayAttrs.description,
    background: (state, getters) => getters.displayAttrs.background,
    portrait: (state, getters) => getters.displayAttrs.portrait,
    format: (state, getters) => getters.displayAttrs.viz,
    user: (state) => state.user,
    newPostLocation: (state, getters) => getters.displayAttrs.new_wish_loc,
    canConfigNewPostLocation: (_, getters) => getters.isStream || getters.isGrid || getters.isMatrix || getters.isShelf,
    filterProfanity: (state, getters) => getters.displayAttrs.filter_profanity,
    requireApproval: (state, getters) => getters.displayAttrs.require_approval,
    // Background/skin, the skin colors are based on the background for now but they may change in the future
    backgroundColor: (state, getters) => getters.displayAttrs.background.dominant_color_as_rgb,
    headerColor: (state, getters) =>
      getters.isMap
        ? !getters.backgroundColor
          ? getters.colorScheme === 'light'
            ? '#FFFFFF'
            : '#000000'
          : getters.backgroundColor
        : getters.isTitleBarTransparent
        ? 'transparent'
        : getters.backgroundColor,
    backgroundLuminance: (state, getters) => getters.displayAttrs.background.luminance_name,
    isBackgroundBlurred: (state, getters) =>
      Array.isArray(getters.background.effects)
        ? getters.background.effects.includes('blur')
        : getters.background.effect === 'blur',
    skinPrimaryColorAsRGB: (state, getters) => getters.displayAttrs.background.primary_color_as_rgb,
    skinAccentColorAsRGB: (state, getters) => getters.displayAttrs.background.accent_color_as_rgb,
    skinPrimaryTextColor: (state, getters) => getters.displayAttrs.background.primary_text_color,
    skinAccentTextColor: (state, getters) => getters.displayAttrs.background.accent_text_color,

    colorScheme: (state, getters) => getters.displayAttrs.color_scheme,
    themeId: (state, getters) => getters.displayAttrs.theme_id, // used for map
    fontId: (state, getters) => getters.displayAttrs.font_id,
    isCommentable: (state, getters) => getters.displayAttrs.is_commentable,
    isReactable: (state, getters) => getters.displayAttrs.is_reactable,
    isRemakeable: (_, getters) => getters.displayAttrs.is_remakeable,
    reactionData: (state, getters) => getters.displayAttrs.reaction_data,
    name: (state, getters) => getters.displayAttrs.name,
    xAuthor: (state, getters) => getters.displayAttrs.show_author,
    isTitleBarTransparent: (state, getters) => getters.displayAttrs.is_title_bar_transparent,

    builder: (state) => state.wall.builder,
    namespace: (state) => state.wall.namespace,
    convertLayoutErrorMessage: (state) => state.convertLayoutErrorMessage,
    isOrgWall: (state) => state.wall.tenant_id > 1,
    coverPostId: (state) => state.wall.cover_wish_id,
    publicKey: (state) => state.wall.public_key,
    address: (state) => state.wall.address,
    domainName: (state) => state.tenant.domain_name,
    tenantName: (state) => state.tenant.name,
    canIAdminister: (state) => state.canIAdminister,
    canIModerate: (state) => state.canIModerate,
    canIWrite: (state) => state.canIWrite,
    canIEdit: (state) => state.canIEdit,
    canIMakeWalls: (state) => state.canIMakeWalls,
    canIRemake: (state) => state.canIRemake,
    canIBookmarkWall: (state, getters) => getters.amIRegistered,
    canIChangeCoverPost: (state) => state.canIAdminister,
    amIOwner: (state) => state.amIOwner,
    amILibraryOwner: (state) => state.amILibraryOwner,
    amICollaborator: (state) => state.amICollaborator,
    amIRegistered: (state) => !!state.user.registered_at,
    isSubscriptionPaused: (state) => state.isSubscriptionPaused,
    canICreateWall: (state) => state.user.quota.can_make,
    canIPostWithoutWaitingForApproval: (state, getters) =>
      getters.canIWrite && (getters.canIModerate || !getters.requireApproval),
    isApp: (state) => state.isApp,
    isPhone: (state) => state.isPhone,
    isDesktop: (state) => state.isDesktop,
    isMousable: (state) => state.isMousable,
    dir: (state) => state.dir,
    isCanvas: (state) => state.wall.viz === 'free',
    isMap: (state) => state.wall.viz === 'map',
    isReadOnly: (state) => state.isReadOnly,
    isScreenshotMode: (state) => state.isScreenshotMode,
    isPostable: (state, getters) => getters.canIWrite && !state.isReadOnly,
    isShelf: (state) => state.wall.viz === 'shelf',
    isStream: (state) => state.wall.viz === 'stream',
    isGrid: (state) => state.wall.viz === 'grid',
    isTable: (state) => state.wall.viz === 'table',
    isTimeline: (state) => state.wall.viz === 'timeline',
    isMatrix: (state) => state.wall.viz === 'matrix',
    isSectioned: (state, getters) => getters.isShelf,
    isPrivate: (_, getters) => getters['wallAccessSettings/isPrivacyTypePrivate'],
    isPasswordProtected: (_, getters) => getters['wallAccessSettings/isPrivacyTypePasswordProtected'],
    isOrgWide: (_, getters) => getters['wallAccessSettings/isPrivacyTypeOrgWide'],
    isLoggedInUsersOnly: (_, getters) => getters['wallAccessSettings/isPrivacyTypeLoggedInUsersOnly'],
    isLibraryWide: (_, getters) => getters['wallAccessSettings/isPrivacyTypeLibraryWide'],
    canHaveConnections: (state, getters) => getters.isCanvas || getters.isMap,
    xPostCommentsInPanel: (state, getters) =>
      getters.isMap || getters.isTable || getters.isCanvas || getters.isTimeline,
    xUnpublishedPostPosition: (state, getters) => getters.isTable,
    uploadLimit: (state) => state.wall.upload_limit,
    isPremiumWall: (state) => state.wall.is_premium, // wall made by a pro user
    links: (state) => state.wall.links,
    xSharePanel: (state, getters) => getters['share/isOpen'],
    xSettingsPanel: (state, getters) => getters['settings/isOpen'],
    isAPanelVisible: (state, getters) =>
      getters.xSharePanel ||
      getters.xSettingsPanel ||
      getters['appBarAccount/xUserMenu'] ||
      getters['alertDialog/isAlertOpen'] ||
      getters['confirmationDialog/isConfirmationOpen'] ||
      getters['onboarding/xReadyToPostPanel'] ||
      getters['contentPicker/xContentPickerPanel'] ||
      getters['postAction/xPostAction'] ||
      getters['plans/xUpgradeModal'] ||
      state.reaction.postIdBeingReactedTo ||
      state.xRemakePanel ||
      state.xContributorsList ||
      state.xConvertLayoutPanel ||
      state.xInfo ||
      state.xToolMenu,
    xHeader: (state) => state.xHeader,
    headerHeight: (state) => state.headerHeight,
    isWallSyncNeeded: (state, getters) => {
      const lastSycnedTime = state.wallLastSyncTime
      if (!lastSycnedTime) return true
      return getters['realtime/isRealtimeDownSinceLastSync'](lastSycnedTime)
    },
    xEditBookmarksDialog: (state) => state.xEditBookmarksDialog,
    editBookmarksDialogPopoverAnchor: (state) => state.editBookmarksDialogPopoverAnchor,

    /* POST MODIFIERS */
    xTallAttachmentPickerBox: (state, getters) => getters.isMap || getters.isTimeline || getters.isTable,
    // pagination - only posts for now
    isInitialPaginatedDataLoadDone: (state, getters) => getters['post/isInitialPaginatedDataLoadDone'],
    nativePostEdit: (state) => state.nativePostEdit,
    modalPostEdit: (state) => state.modalPostEdit,
    xNewComposerModalPanel: (_, getters) => !getters.nativePostEdit && getters.canIWrite && !getters.isReadOnly,
    isUsingNewSurfaceShadows: (_) => isAppUsing('newSurfaceShadows'),

    /* Surface Layout */
    xDesktopSurfaceLayoutV3: (_, getters) =>
      isAppUsing('surfaceLayoutV3') && !getters.isApp && !getters['browser/isSmallerThanTabletLandscape'],
    xMobileSurfaceLayoutV3: (_, getters) =>
      isAppUsing('surfaceLayoutV3') && !getters.isApp && getters['browser/isSmallerThanTabletLandscape'],
    canFilterPosts: (_, getters) => isAppUsing('surfaceFilter') && !getters.isApp,

    isAPostDragging: (state) => state.isAPostDragging,
    /* User Oauth Token */
    oauthToken: (state) => state.arvoConfig && state.arvoConfig.token && state.arvoConfig.token.oauthToken,

    //Libraries
    isLibraryWall: (state) => !!state.wall.library_id,
    wallLibraryId: (state) => state.wall.library_id,
    creatableLibraries: (state) => sortBy(state.creatableLibraries, 'joinedAt'),
    creatableLibraryById: (_, getters) => (libraryId) =>
      getters.creatableLibraries.find((creatableLibrary) => creatableLibrary?.id == libraryId),
    viewableLibraries: (state) => sortBy(state.viewableLibraries, 'joinedAt'),
    wallInCreatableLibraries: (state, getters) =>
      getters.creatableLibraries.map((library) => parseInt(library.id)).includes(state.wall.library_id),
    library: (state) => state.library,
    libraryName: (_, getters) => getters.library?.name,
    librarySlug: (_, getters) => getters.library?.slug,
    libraryMakersCount: (_, getters) => getters.library?.makers_count,
    libraryWallsUsed: (_, getters) => getters.library?.walls_used,
    organizationName: (_, getters) => (getters.isLibraryWall ? getters.libraryName : getters.tenantName),
    // Embed
    loginAndRedirectToWallUrl: (state) => state.loginAndRedirectToWallUrl,
    homeUrl: (state) => state.homeUrl,

    isRemakeToggleable: () => isAppUsing('isRemakeToggleable'),
  },
  mutations: {
    INITIALIZE_STATE(state, data) {
      Object.assign(state, data)
    },
    CHANGE_CONNECTIVITY(state, data) {
      state.isOnline = data.isOnline
    },
    CHANGE_DRAGGING(state, { isAPostDragging }) {
      state.isAPostDragging = isAPostDragging
    },
    HIDE_CONTRIBUTORS_LIST(state) {
      state.xContributorsList = false
    },
    HIDE_EXTENSION_REQUIRED(state) {
      state.xExtensionRequired = false
    },
    UPDATE_FILE_THAT_EXCEEDED_SIZE(state, file) {
      state.fileThatExceededSize = file
    },
    HIDE_INFO(state) {
      state.xInfo = false
    },
    HIDE_WALL_QUOTA_EXCEEDED(state) {
      state.xWallQuotaExceeded = false
    },
    HIDE_COMMENTS_PANEL(state) {
      state.xCommentsPanel = false
      state.commentsPanelPostId = null
    },
    HIDE_PERMIT_NOTIFICATION(state) {
      state.xPermitNotification = false
    },
    SHOW_RELOAD_NOTIFICATION(state) {
      state.xReloadNotification = true
    },
    HIDE_RELOAD_NOTIFICATION(state) {
      state.xReloadNotification = false
    },
    SHOW_CONTRIBUTORS_LIST(state) {
      state.xContributorsList = true
    },
    SHOW_EXTENSION_REQUIRED(state) {
      state.xExtensionRequired = true
    },
    SHOW_INFO(state) {
      state.xInfo = true
    },
    SHOW_WALL_QUOTA_EXCEEDED(state) {
      state.xWallQuotaExceeded = true
    },
    SHOW_COMMENTS_PANEL(state, postId) {
      state.xCommentsPanel = true
      state.commentsPanelPostId = postId
    },
    SHOW_PERMIT_NOTIFICATION(state) {
      state.xPermitNotification = true
    },
    RESIZE_EDITOR(state, height) {
      state.editorHeight = height
    },
    RESIZE_HEADER(state, height) {
      state.headerHeight = height
    },
    SHOW_CONVERT_LAYOUT_PANEL(state) {
      state.xConvertLayoutPanel = true
    },
    HIDE_CONVERT_LAYOUT_PANEL(state) {
      state.xConvertLayoutPanel = false
    },
    CONVERT_LAYOUT_FAILED(state, message) {
      state.convertLayoutErrorMessage = message
    },
    SHOW_TOOL_MENU(state) {
      state.xToolMenu = true
    },
    HIDE_TOOL_MENU(state) {
      state.xToolMenu = false
    },
    SHOW_REMAKE_PANEL(state) {
      state.xRemakePanel = true
    },
    HIDE_REMAKE_PANEL(state) {
      state.xRemakePanel = false
    },
    SHOW_EDIT_BOOKMARKS_DIALOG(state, anchor = null) {
      if (anchor) state.editBookmarksDialogPopoverAnchor = anchor
      state.xEditBookmarksDialog = true
    },
    HIDE_EDIT_BOOKMARKS_DIALOG(state) {
      state.xEditBookmarksDialog = false
    },

    UPDATE_WALL(state, changes) {
      // Do this so that the whole wall object will not be reactively updated
      // but rather just each of the individual properties will be.
      Object.keys(changes).forEach((c) => {
        Vue.set(state.wall, c, changes[c])
      })
    },
    ANNOUNCE_WISH_LIST_MOUNTED(state) {
      state.wishListMounted = true
    },
    REFRESH_REQUESTED(state) {
      state.isRefreshPending = true
    },
    REFRESH_DONE(state) {
      state.isRefreshPending = false
      state.wallLastSyncTime = Date.now()
    },
    SET_CREATABLE_LIBRARIES(state, { libraries }) {
      state.creatableLibraries = libraries
    },
    SET_VIEWABLE_LIBRARIES(state, { libraries }) {
      state.viewableLibraries = libraries
    },
  },
  actions: {
    initializeState({ commit, dispatch, getters }, data) {
      const {
        isFirstView,
        postAction: { expandedPostCid },
        brahmsToken,
        deviceId,
        firstViewAfterInviteAccepted,
        ...rootStoreState
      } = data

      const userId = rootStoreState.user.id
      const wallId = rootStoreState.wall.id

      commit('INITIALIZE_STATE', rootStoreState)

      dispatch('browser/initializeState')
      dispatch('onboarding/setIsFirstView', isFirstView)
      dispatch('realtime/initializeState', {
        brahmsToken,
        deviceId,
        pingContributingStatusToken: rootStoreState.constants.pingContributingStatusToken,
        userId,
      })
      if (expandedPostCid) {
        if (!getters['post/postEntitiesByCid'][expandedPostCid]) {
          // When a post is opened from URL, rootGetters['postAction/expandedPost'] would be null
          // since fetchWishes would have not finished
          dispatch('post/fetchSinglePost', { postId: parseInt(expandedPostCid.replace(/\D/g, ' '), 10) }).finally(
            () => {
              // Sometimes at this point all wishes are fetched. As long as the expanded post is fetched,
              // we can expand and show it first
              dispatch('postAction/expandPost', { postCid: expandedPostCid, xTextPanel: true })
            },
          )
        } else {
          dispatch('postAction/expandPost', { postCid: expandedPostCid, xTextPanel: true })
        }
      }
      dispatch('electronApp/setCurrentUser', rootStoreState.user)
      if (getters.amIRegistered) {
        if (getters.amIOwner) {
          dispatch('plans/fetchPlans')
          dispatch('plans/fetchAdvertisedQuotas')
        }

        dispatch('wallCollections/setActiveUser', userId)
        dispatch('wallCollections/switchAccountKey', { type: 'user', id: userId })
        dispatch('wallCollections/setActiveWall', wallId)
        dispatch('wallCollections/fetchBookmarks', { wallId })
        dispatch('wallCollections/fetchFolders', { userId })
      }

      // Show invite accepted toast if first view after invite accepted.
      if (isAppUsing('newInvites') && firstViewAfterInviteAccepted) {
        dispatch('snackbar', {
          notificationType: SnackbarNotificationType.success,
          message: __('Invite accepted'),
          timeout: 3000,
        })
      }

      // Perform actions based on location hash, e.g. open panels
      watchHash()
    },
    reloadPage() {
      reload()
    },
    changeConnectivity({ commit }, data) {
      commit('CHANGE_CONNECTIVITY', data)
    },
    changeDragging({ commit }, { isAPostDragging }) {
      commit('CHANGE_DRAGGING', { isAPostDragging })
    },
    /* ------------
     * PANEL
     * ------------ */
    hideContributorsList({ commit }) {
      commit('HIDE_CONTRIBUTORS_LIST')
    },
    hideExtensionRequired({ commit }) {
      commit('HIDE_EXTENSION_REQUIRED')
    },
    hideInfo({ commit }) {
      commit('HIDE_INFO')
    },
    hideWallQuotaExceeded({ commit }) {
      commit('HIDE_WALL_QUOTA_EXCEEDED')
    },
    hideCommentsPanel({ commit }) {
      commit('HIDE_COMMENTS_PANEL')
    },
    hideToolMenu({ commit }) {
      commit('HIDE_TOOL_MENU')
    },
    hidePermitNotification({ commit }) {
      commit('HIDE_PERMIT_NOTIFICATION')
    },
    showReloadNotification({ commit }) {
      commit('SHOW_RELOAD_NOTIFICATION')
    },
    hideReloadNotification({ commit }) {
      commit('HIDE_RELOAD_NOTIFICATION')
    },
    showContributorsList({ commit }) {
      commit('SHOW_CONTRIBUTORS_LIST')
    },
    showExtensionRequired({ commit }) {
      commit('SHOW_EXTENSION_REQUIRED')
    },
    showOverFileSizeModal({ commit, dispatch, getters }, { file }) {
      dispatch('post/rejectPostsWithFileSizeExceeded', { file })
      commit('UPDATE_FILE_THAT_EXCEEDED_SIZE', file)
      if (getters.isLibraryWall) {
        const MAX_UPLOAD_BYTES = 1200000000 // 1.2GB
        if (!getters.amILibraryOwner) {
          dispatch('triggerOverFileSizeAlertDialog')
        } else {
          if (file.size > MAX_UPLOAD_BYTES) {
            dispatch('triggerOverFileSizeAlertDialog')
          } else {
            dispatch('libraryPlans/fetchPlansAndShowUpgradeModal', {
              upgradeStep: LibraryUpgradeStep.ChooseTierFileSizeQuota,
              libraryId: getters.wallLibraryId,
              makersCount: getters.libraryMakersCount,
              wallsUsed: getters.libraryWallsUsed,
            })
          }
        }
      } else {
        const MAX_UPLOAD_BYTES = 600000000 // 600MB
        if (!getters.amIOwner) {
          dispatch('triggerOverFileSizeAlertDialog')
        } else {
          if (file.size > MAX_UPLOAD_BYTES) {
            dispatch('triggerOverFileSizeAlertDialog')
          } else {
            dispatch('plans/showPlanUpgradeModalCheckingScheduledChanges', {
              step: UpgradeStep.ChooseTierFileSizeQuota,
              membershipTier: getters.user.membership_tier,
              paymentSchedule: getters.user.period,
              upgradeSource: UpgradeSource.FileSizeLimit,
            })
          }
        }
      }
    },
    triggerOverFileSizeAlertDialog({ getters, dispatch }) {
      dispatch('alertDialog/openAlertDialog', {
        iconSrc: anxiousFace,
        iconAlt: __('Anxious face'),
        title: __('File too large!'),
        body: __('This Padlet has a file size limit of %{num}.', {
          num: numberToHumanSize(getters.uploadLimit, { precision: 2, strip_insignificant_zeros: true }),
        }),
        xShadow: getters.isUsingNewSurfaceShadows,
      })
    },
    showInfo({ commit }) {
      commit('SHOW_INFO')
    },
    showWallQuotaExceeded({ commit }) {
      commit('SHOW_WALL_QUOTA_EXCEEDED')
    },
    showCommentsPanel({ commit }, { postId }) {
      commit('SHOW_COMMENTS_PANEL', postId)
    },
    showDeleteDialog({ dispatch, getters }) {
      dispatch('confirmationDialog/openConfirmationDialog', {
        ...deleteWallConfirmationParams(getters.title),
        afterConfirmActions: [{ type: 'deleteWall' }],
        xShadow: getters.isUsingNewSurfaceShadows,
      })
    },
    showClearAllDialog({ dispatch, getters }) {
      dispatch('confirmationDialog/openConfirmationDialog', {
        isCodeProtected: true,
        isConfirmActionDanger: true,
        title: __('Delete all posts?'),
        body: __('This action cannot be undone.'),
        confirmButtonText: __('Delete'),
        cancelButtonText: __('Nevermind'),
        afterConfirmActions: [{ type: 'post/clearAllPosts' }],
        xShadow: getters.isUsingNewSurfaceShadows,
        shouldFadeIn: false,
      })
    },
    checkAndSubscribeToPushNotifications({ dispatch }) {
      getNotificationPermission().then((permission) => {
        if (permission === 'granted') {
          dispatch('registerForPushNotifications')
        } else if (permission === 'default') {
          // Need user to trigger the request
          const message = __('Turn on notifications for this browser?')
          const actionText = __('OK')
          const actionName = 'registerForPushNotifications'
          dispatch(
            'snackbars/setSnackbar',
            { message, actionText, actionName, timeout: 6000, showCloseButton: true },
            { root: true },
          )
        }
        // Respect the user's decision if denied
        // else if (permission === 'denied') {
        //   // Displays unblocking instructions
        //   dispatch('registerForPushNotifications')
        // }
      })
    },
    registerForPushNotifications({ commit, state }) {
      registerForPushNotifications(state.user).catch((e) => {
        console.warn(`No push`, e)
        commit('SHOW_PERMIT_NOTIFICATION')
      })
    },
    showConvertLayoutPanel({ commit }) {
      commit('SHOW_CONVERT_LAYOUT_PANEL')
    },
    hideConvertLayoutPanel({ commit }) {
      commit('HIDE_CONVERT_LAYOUT_PANEL')
    },
    async convertWallLayout({ commit, getters }, { newLayout }) {
      try {
        await PadletApi.Wall.convertLayout({
          id: getters.wallId,
          newLayout,
        })
        commit('UPDATE_WALL', { viz: newLayout })
      } catch {
        const errorMessage = __('Failed to convert layout. Please try again.')
        commit('CONVERT_LAYOUT_FAILED', errorMessage)
      }
    },
    showRemakePanel({ commit }) {
      commit('SHOW_REMAKE_PANEL')
    },
    hideRemakePanel({ commit }) {
      commit('HIDE_REMAKE_PANEL')
    },
    toggleToolMenu({ commit, state }) {
      if (state.xToolMenu) {
        commit('HIDE_TOOL_MENU')
      } else {
        commit('SHOW_TOOL_MENU')
      }
    },
    /* ------------
     * POST
     * ------------ */
    deletePostRemote({ dispatch, state }, post) {
      if (state.commentsPanelPostId == post.id) {
        dispatch('hideCommentsPanel')
      }
      // postAction/deletePostRemote must come before post/deletePostRemote
      // the post action panels(e.g.post action menu, copy / transfer panels) hold their own cid value and will try to access getPostByCid(cid)
      // if we delete the post first, it will cause some undefined object errors
      dispatch('postAction/deletePostRemote', post)
      dispatch('post/deletePostRemote', post)
    },
    removePost({ dispatch }, { id, cid }) {
      dispatch('post/removePost', { id, cid })
    },
    addPost({ dispatch }, payload) {
      const postAttributes = payload.post
      dispatch('post/addPost', postAttributes)
    },
    /* ------------
     * ATTACHMENT
     * ------------ */
    resizeEditor({ commit }, { height }) {
      commit('RESIZE_EDITOR', height)
    },

    /* ------------
     * HEADER
     * ------------ */
    resizeHeader({ commit }, { height }) {
      commit('RESIZE_HEADER', height)
    },

    /* ------------
     * WALL
     * ------------ */
    updateWall({ commit }, changes) {
      commit('UPDATE_WALL', changes)
    },
    async setCoverPost({ commit, dispatch, state }, { postId }) {
      const wall = await saveWall({ ...state.wall, cover_wish_id: postId })
      commit('UPDATE_WALL', wall)
      dispatch('snackbar', {
        notificationType: SnackbarNotificationType.success,
        message: __('Cover image updated'),
      })
    },
    async unsetCoverPost({ commit, dispatch, state }) {
      const wall = await saveWall({ ...state.wall, cover_wish_id: null })
      commit('UPDATE_WALL', wall)
      dispatch('snackbar', {
        notificationType: SnackbarNotificationType.success,
        message: __('Cover image updated'),
      })
    },
    async archiveWall({ getters }) {
      const payload = { mutation: ARCHIVE_PADLET, variables: { wallId: getters.wallId } }
      await apolloClient.mutate(payload)
      safeStorage.setItem('activePadletFilter', 'archived')
      navigateTo('/')
    },
    async deleteWall({ getters }) {
      await deleteWall(getters.wallId)
      navigateTo('/')
    },

    /*****************
     * BOOKMARKS
     *****************/

    async bookmarkWallAndNotify({ dispatch, getters }) {
      const wallId = getters.wallId

      try {
        await dispatch('wallCollections/addBookmark', { wallId })
        await dispatch('snackbar', {
          notificationType: SnackbarNotificationType.success,
          message: __('Bookmark added'),
          actionText: __('Edit'),
          actionTextActions: [{ type: 'openEditBookmarksDialog' }],
        })
      } catch (error) {
        dispatch('globalSnackbar/genericFetchError', { error })
      }
    },
    async addToFavorites({ dispatch, getters }) {
      const wallId = getters.wallId
      try {
        await dispatch('wallCollections/addBookmark', { wallId })
      } catch (error) {
        dispatch('globalSnackbar/genericFetchError', { error })
      }
    },
    async removeFromFavorites({ dispatch, getters }) {
      const wallId = getters.wallId

      try {
        await dispatch('wallCollections/removeBookmark', { wallId })
      } catch (error) {
        dispatch('globalSnackbar/genericFetchError', { error })
      }
    },
    openEditBookmarksDialog({ commit }, anchor = null) {
      commit('SHOW_EDIT_BOOKMARKS_DIALOG', anchor)
    },
    closeEditBookmarksDialog({ commit }) {
      commit('HIDE_EDIT_BOOKMARKS_DIALOG')
    },
    promptNameForNewFolder({ dispatch }) {
      dispatch('inputDialog/openInputDialog', {
        title: __('New folder'),
        inputPlaceholder: __('Folder name'),
        inputMaxLength: MAX_FOLDER_NAME_LENGTH,
        validationActions: [{ type: 'validateFolderName' }],
        submitActions: [{ type: 'createFolder' }],
      })
    },
    async createFolder({ dispatch }, { inputValue }) {
      const { success } = await dispatch('wallCollections/createFolderForActiveUser', {
        name: inputValue,
      })
      if (success) {
        Vue.nextTick(() => {
          dispatch('snackbar', {
            notificationType: SnackbarNotificationType.success,
            message: __('Created new folder'),
          })
        })
      }
    },
    validateFolderName({ dispatch }, { inputValue, disallowedNames }) {
      const disable = !isValidFolderName(inputValue) || (disallowedNames || []).includes(inputValue)
      dispatch('inputDialog/setSubmitButtonDisable', { disable })
    },

    // SCROLL
    announceWishListMounted({ commit }) {
      commit('ANNOUNCE_WISH_LIST_MOUNTED')
    },
    /* ------------
     * POST CONNECTION
     * ------------ */
    removeConnectionsForPost({ dispatch }, { postId }) {
      dispatch('postConnection/removeConnectionsForPost', { postId })
    },

    // REFRESH
    requestRefresh({ commit, dispatch, state }, { isAutoRefresh }) {
      if (state.isRefreshPending) return
      commit('REFRESH_REQUESTED')
      dispatch('refresh', { isAutoRefresh })
    },
    refresh({ commit, dispatch, state, getters }, { isAutoRefresh }) {
      if (!state.isOnline) return
      if (!getters.isWallSyncNeeded) return

      // `refresh` will apply all the pending realtime updates.
      // This is not immune to race conditions, but since it is harmless to omit this is the first place,
      // this is sufficient to reduce user annoyance due to outdated notifications.
      dispatch('realtime/clearDispatchQueue')

      const mapWasFittingMarkerBounds = getters['map/mapDoesFitMarkersBounds']
      dispatch('map/disableMapFittingMarkersBounds')

      // We are not updating the vuexstore on auto refresh, the network response will update service worker cache
      // and if the cache is update, it will send a notification to update the store

      const updateOrderAndMapIfNecessary = () => {
        if (isAppUsing('wishOrderArray')) {
          dispatch('post/fetchPostOrder')
        }
        if (mapWasFittingMarkerBounds) {
          dispatch('map/enableMapFittingMarkersBounds')
        }
      }

      const shouldResetState = !isAutoRefresh
      dispatch('reaction/fetchReactions', { shouldResetState })
      dispatch('comment/fetchComments', { shouldResetState })
      if (isAutoRefresh) {
        dispatch('post/pingPosts').then(updateOrderAndMapIfNecessary)
      } else {
        dispatch('post/fetchPosts', { shouldResetState }).then(updateOrderAndMapIfNecessary)
      }
      if (getters.isSectioned) dispatch('section/fetchSections')
      if (getters.canHaveConnections) dispatch('postConnection/fetchConnections')

      dispatch('contributingStatus/ping')

      // reactions, comments, wall sections, ... only do 1 request
      // but wishes have pagination and may make multiple requests
      // => for first page load, wait for it to be done before marking things as done
      if (getters.isInitialPaginatedDataLoadDone) {
        commit('REFRESH_DONE')
      } else {
        const unwatch = this.watch(
          (state, getters) => getters.isInitialPaginatedDataLoadDone,
          (newValue, oldValue) => {
            if (newValue) {
              commit('REFRESH_DONE')
              unwatch()
            }
          },
        )
      }
    },

    //LIBRARIES
    async fetchCreatableLibraries({ commit }, { userId }) {
      const response = await PadletApi.Library.fetchCreatableLibraries({ userId: userId })
      const librariesData = response.data as Array<JsonAPIResource<Library>>
      commit('SET_CREATABLE_LIBRARIES', { libraries: librariesData.map((library) => library.attributes) })
    },
    async fetchViewableLibraries({ commit }, { userId }) {
      const response = await PadletApi.Library.fetchViewableLibraries({ userId: userId })
      const librariesData = response.data as Array<JsonAPIResource<Library>>
      commit('SET_VIEWABLE_LIBRARIES', { libraries: librariesData.map((library) => library.attributes) })
    },

    // SNACKBAR
    snackbar({ dispatch }, snackbarPayload) {
      dispatch('globalSnackbar/setSnackbar', snackbarPayload)
    },

    loginAndRedirectTo(_, link) {
      navigateTo(loginWithReferrerUrl(link))
    },
  },
}

const store = new Vuex.Store(storeStructure)

const oldDispatch = (obj) => store.dispatch(obj.type, obj)

// Define a local type for now
interface BrowserWindow {
  ww?: any
}
const thisWindow = window as unknown as BrowserWindow

/**
 * The old dispatch function signature is different from the vuex store dispatch function signature
 * We write the dispatch function in ww.xstore so that any method signature will work.
 * ```js
 * ww.xstore.dispatch({type: 'START_EDITING_POST', payload: {postCid: id}})
 * ww.xstore.dispatch('START_EDITING_POST', {postCid: id})
 * ww.xstore.dispatch(function (dispatch) => {dispatch({type: 'START_EDITING_POST', payload: {postCid: id}})}
 * ```
 */
if (!thisWindow.ww) {
  thisWindow.ww = {}
}
thisWindow.ww.xstore = {
  store: store,
  state: {},
  getters: {},
  dispatch: (obj, payload) => {
    if (typeof obj === 'string') {
      store.dispatch(obj, payload)
    } else if (obj.type) {
      oldDispatch(obj)
    } else if (typeof obj === 'function') {
      obj(oldDispatch)
    }
  },
}

store.subscribe(() => {
  thisWindow.ww.xstore.state = store.state
  thisWindow.ww.xstore.getters = store.getters
})

store.watch(
  (_state, getters) => getters.isNewComposerModalOpened,
  (newValue, oldValue) => {
    if (!oldValue && newValue) {
      store.dispatch('contributingStatus/updateContributingStatus', { status: 1 })
    } else if (oldValue && !newValue) {
      store.dispatch('contributingStatus/updateContributingStatus', { status: 0 })
    }
  },
)

export default store
